"use client";

import {
  PersistentMedia,
  usePersistentPlayer,
} from "./PersistentPlayerProvider";
import { useEffect, useRef } from "react";

import { getCDNImageURL } from "@/data/images";

/**
 * The player's own video-box height. `vjs-16-9` derives it from the VIEWPORT
 * width, capped so a wide desktop viewport doesn't make it tall — kept in sync
 * with `.video-js.vjs-16-9` / `.vjs-wrapper` in VideoJS-theme.css. Used to
 * reserve space before the host reports its real rendered height (which also
 * includes the control bar). A plain `16/9` box here over-reserves on desktop.
 */
const PLAYER_BOX_HEIGHT = "min(calc(100vw * 9 / 16), calc(100vh - 200px))";

/**
 * A page-side placeholder that reserves the hero's space and tells the
 * persistent host (a) which special this page wants playing and (b) where to
 * position the player. The host renders the actual `<video>` over this box, so
 * navigating between special pages never unmounts the player. Until the player
 * is rendered over it, the box shows the special's poster (not a black void).
 *
 * Full-width by design — it sits inside the page's `videoplayerwrapper`, exactly
 * like the inline player (the page's max-width applies only to the chrome below).
 */
export function HeroSlot({ media }: { media: PersistentMedia }) {
  const ref = useRef<HTMLDivElement>(null);
  const { setMedia, registerSlot, playerHeight } = usePersistentPlayer();

  // Push this page's media to the host. Keyed on the special id (+ access) so it
  // only re-fires when the page actually changes what should be playing.
  useEffect(() => {
    setMedia(media);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media.special.id, media.userCanAccessSpecial, setMedia]);

  // Register the placeholder element so the host can track its rect, and clear
  // it on unmount so the host hides when no special page is mounted.
  useEffect(() => {
    registerSlot(ref.current);
    return () => registerSlot(null);
  }, [registerSlot]);

  // Same image the player itself will load as its poster, so the placeholder
  // matches what appears once the <video> overlays it.
  const poster = media.special.preview_img
    ? getCDNImageURL(media.special.preview_img)
    : media.posterImg;

  // Reserve the player's true height once the host reports it (16:9 video +
  // control bar); fall back to the matching capped box until then.
  return (
    <div
      ref={ref}
      className="w-full bg-black bg-cover bg-center"
      style={{
        height: playerHeight ?? PLAYER_BOX_HEIGHT,
        ...(poster ? { backgroundImage: `url("${poster}")` } : {}),
      }}
      aria-hidden
    />
  );
}
